import { forwardRef } from 'react'
import { Popover as PopoverPrimitive } from '@base-ui-components/react/popover'
import styled, { createGlobalStyle, css, keyframes } from 'styled-components'

import { Box } from 'ui-primitives/src/Box'
import { useBreakpoints } from 'ui-primitives/src/hooks/useBreakpoints'
import { breakpoints } from 'ui-primitives/src/utils/breakpoints'
import { media } from 'ui-primitives/src/utils/media'
import { spacing } from 'ui-primitives/src/utils/spacing'
import { zIndex } from 'bl-common/src/constants/zIndex'
import { CloseIcon } from 'bl-common/src/elements/Icons/CloseIcon'
import { useFormatMessage } from 'bl-localization/src/useFormatMessage'
import { Text } from 'flow-builder/src/components/Text/Text'

import { bookingBarMessages } from './messages'

export type PopoverContentProps = {
  anchor?: HTMLDivElement | null
  mobileFooterContent?: JSX.Element
  onBack?: () => void
  onNext?: () => void
}

export type PopoverState = {
  open: boolean
  setOpen: (open: boolean) => void
}

const Popover = PopoverPrimitive.Root

const PopoverContent = forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Popup>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Popup> &
    PopoverContentProps
>(
  (
    { children, anchor, mobileFooterContent, onBack, onNext, ...props },
    ref
  ) => {
    const { isMobile } = useBreakpoints()
    const { formatMessage } = useFormatMessage()

    return (
      <PopoverPrimitive.Portal>
        <Backdrop />
        <PopoverPrimitive.Positioner anchor={anchor} collisionPadding={0}>
          <Wrapper ref={ref} {...props}>
            <Content>
              {isMobile && (
                <Box
                  padding={spacing[2]}
                  flexDirection="row"
                  style={{ borderBottom: '1px solid var(--border-color)' }}
                >
                  {onBack && (
                    <Box alignItems="flex-start" justifyContent="center">
                      <BackButton onClick={onBack}>
                        <BackArrow />
                      </BackButton>
                    </Box>
                  )}
                  <Box alignItems="flex-end">
                    <CloseButton>
                      <CloseIcon
                        color="currentColor"
                        style={{
                          width: '12px',
                          height: '12px',
                        }}
                      />
                    </CloseButton>
                  </Box>
                </Box>
              )}
              <Box flex="1" style={{ overflowX: 'hidden', overflowY: 'auto' }}>
                {children}
              </Box>
              {isMobile && (
                <Box
                  flexDirection="row"
                  background="var(--color-background)"
                  boxShadow="var(--box-shadow)"
                >
                  {mobileFooterContent && (
                    <Box justifyContent="center" padding={spacing[2]}>
                      {mobileFooterContent}
                    </Box>
                  )}
                  <Box justifyContent="center" padding={spacing[2]}>
                    <ContinueButton onClick={onNext} disabled={!onNext}>
                      {formatMessage(bookingBarMessages.button.continue)}
                    </ContinueButton>
                  </Box>
                </Box>
              )}
            </Content>
            <Styles />
          </Wrapper>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    )
  }
)

const Wrapper = styled(PopoverPrimitive.Popup)`
  background-color: var(--color-fillPrimary);

  ${media.md`
    width: var(--anchor-width);
  `}
`

const Content = styled(Box)`
  height: 100vh;
  height: 100dvh;
  box-shadow: var(--box-shadow);

  ${media.md`
    height: auto;
  `}
`

const CloseButton = styled(PopoverPrimitive.Close)`
  width: 22px;
  height: 22px;
  padding: 5px;
  color: var(--color-text);
  cursor: pointer;
  transition: transform 200ms ease-out;
  transform: rotateZ(-90deg);
  outline: none;
  
  &:hover {
    transform: none;
  }
`

const BackButton = styled(PopoverPrimitive.Close)`
  display: flex;
  color: var(--color-accent);
  cursor: pointer;
  justify-content: center;
  align-items: center;

  svg {
    padding-top: 0;
    fill: var(--color-accent);
  }
`

const ContinueButton = styled(PopoverPrimitive.Close)`
  background: var(--color-buttonPrimaryBackground);
  color: var(--color-buttonPrimaryText);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  height: 45px;
  
  &[disabled] {
    background: var(--color-buttonDisabled);
    color: var(--color-text);
    cursor: default;
  }
`

const PopoverTrigger = ({
  title,
  value,
  onClick,
  selected,
}: {
  title: string
  value: string
  onClick?: (event: React.MouseEvent) => void
  selected?: boolean
}) => (
  <Button {...(onClick && { onClick })} selected={selected}>
    <Text
      preset="label"
      type="text"
      as="span"
      value={title}
      color="var(--color-accent)"
    />
    <Text
      preset="text"
      type="text"
      as="span"
      value={value}
      color="var(--color-text)"
    />
  </Button>
)

const Button = styled.button<{
  selected?: boolean
}>`
  ${({ selected }) => css`
    width: 100%;
    padding: ${spacing[1]} ${spacing[2]};
    background-color: var(--color-fillPrimary);
    border-top: 4px solid transparent;
    text-align: left;
    cursor: pointer;
    outline: none;

    ${media.md`
      border-top-color: ${selected ? 'var(--color-accent)' : 'transparent'};

      &:hover {
        border-top-color: var(--color-accent);
      }
    `}

    & span {
      width: 100%;

      &:first-of-type {
        display: inline-block;
        padding-bottom: ${spacing[0.5]};
     }
   }
  `}
`
const BackArrow = () => (
  <svg
    width="13"
    height="10"
    viewBox="0 0 13 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12.2074 5.78947H3.00079L6.10369 8.89238L4.99607 10L0 5.00393L1.10762 3.89631L4.99607 0L6.10369 1.11548L3.00079 4.21838H12.2074V5.78947Z" />
  </svg>
)

const slideIn = keyframes`
  from {
    opacity: 0.2;
    transform: translateY(400px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const slideOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0.2;
    transform: translateY(200px);
  }
`

const Backdrop = styled(PopoverPrimitive.Backdrop)`
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  position: fixed;
  z-index: ${zIndex.modal};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  
  ${media.md`
    display: none;
  `}
`

const Styles = createGlobalStyle`
  body {
    position: relative;
  }
  [data-floating-ui-portal] {
    div[role=presentation]:not([data-floating-ui-inert]) {
      z-index: ${zIndex.behindMenu};

      @media (max-width: ${breakpoints.md}px) {
        z-index: ${zIndex.modal};
        transform: none !important;
        bottom: 0;
        top: 0 !important;
        left: 0 !important;
        right: 0;
        position: fixed !important;

        [data-open] {
          animation: ${slideIn} 400ms ease-out;
        }
        
        [data-closed] {
          animation: ${slideOut} 300ms ease-in;
        }
      }

      @media (min-width: ${breakpoints.mlg}px) {
        z-index: ${zIndex.aboveMenu};
      }
    }
  }  
`

export { Popover, PopoverTrigger, PopoverContent }
